<template>
  <ontos-form-dialog :title="title" :subtitle="subtitle" max-width="560px" :loaded="!loading"
                     :saving="saving" :save-disabled="!chosen.length" :dirty="chosen.length > 0"
                     @save="$emit('link', chosen)" @closed="$emit('closed')">
    <!-- Os já vinculados não aparecem: escolher de novo um deles não mudaria nada, e a lista
         ficaria mais longa justamente com o que não serve. -->
    <p v-if="!candidates.length" class="text-body-2 text-medium-emphasis">
      {{ $tt('relationship-link-none', {collection}) }}
    </p>
    <!-- O desenho de todo campo de relacionamento — a régua na opção e no escolhido, sem chip —, e
         não o chip do Vuetify, que deixava esta janela fora do padrão (relato do usuário). -->
    <v-autocomplete v-else v-model="chosen" :items="candidates" :label="collection"
                    item-title="_identifier" item-value="_instance"
                    multiple autofocus auto-select-first>
      <template #item="{item, props}">
        <ontos-rel-option v-bind="props" :item="item"/>
      </template>
      <template #selection="{item}">
        <ontos-rel-selection :item="item" removable @remove="remove($event)"/>
      </template>
    </v-autocomplete>
    <!-- A restrição, quando aberta pelo "+" de uma raia: o valor da raia com o MESMO chip que a raia e
         o rótulo de bloco usam (escolha do usuário entre chip, dica e subtítulo). EMBAIXO da escolha
         (pedido do usuário), como uma dica do campo — e embaixo do aviso de quando não há nada, a
         que também se aplica. O clique no item de uma raia de relacionamento não sai daqui: a janela
         está no meio de uma escolha. -->
    <div v-if="restriction.length" class="d-flex align-center flex-wrap ga-4">
      <ontos-lane-value v-for="value of restriction" :key="value.dimension.key" named
                        :value="value" :context="targetContext" @link="$event.event.preventDefault()"/>
    </div>
  </ontos-form-dialog>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import RelOption from '@/components/live/instance/RelOption.vue'
import RelSelection from '@/components/live/instance/RelSelection.vue'
import {DomainItem} from '@/services/reverseRelationship'
import {optionIdOf} from '@/utils/propertyDisplay'
import LaneValue from '@/components/live/instance/LaneValue.vue'
import {CoordinateValue} from '@/utils/kanbanDimensions'
import {InstanceContext} from '@/types/InstanceContext'

/**
 * Escolher itens que JÁ existem para vincular a um dono — o "Vincular existente" da seção de um
 * relacionamento na ficha. Vários de uma vez (escolha do usuário), com busca.
 *
 * Só escolhe: quem grava, e pergunta antes de roubar o vínculo de outro dono, é quem abriu (ver o
 * `RelationshipAdd`). Daqui sai o `link` com os ids.
 */
@Component({
  emits: ['link', 'closed'],
  name: 'ontos-link-existing-dialog',
  components: {'ontos-rel-option': RelOption, 'ontos-rel-selection': RelSelection, 'ontos-lane-value': LaneValue}
})
class LinkExistingDialog extends Vue {
  /** Os itens que podem ser vinculados — sem os que já estão. */
  @Prop({type: Array, required: true}) readonly candidates!: DomainItem[]
  /** O nome da coleção no plural: título, rótulo do campo e o aviso de quando não há nada. */
  @Prop({type: String, required: true}) readonly collection!: string
  /** Os valores da raia, quando aberta pelo "+" de uma: o que os candidatos têm em comum. */
  @Prop({type: Array, default: () => []}) readonly restriction!: CoordinateValue[]
  /** A coleção dos candidatos — é para onde aponta o chip, quando a raia é um item. */
  @Prop({type: Object, default: null}) readonly targetContext!: InstanceContext | null
  @Prop({type: String, default: ''}) readonly title!: string
  /** O dono, por extenso — a quem os itens vão ser vinculados. */
  @Prop({type: String, default: ''}) readonly subtitle!: string
  @Prop({type: Boolean, default: false}) readonly saving!: boolean
  /** Os candidatos ainda estão chegando — os de uma raia vêm do servidor (ver o `RelationshipAdd`). */
  @Prop({type: Boolean, default: false}) readonly loading!: boolean

  chosen: number[] = []

  remove(item: any) {
    const id = optionIdOf(item)
    this.chosen = this.chosen.filter(chosen => Number(chosen) !== id)
  }
}

export default toNative(LinkExistingDialog)
</script>
